import {
  keepPreviousData,
  queryOptions,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query'

import { screenerKeys } from '@/features/screener/api/keys'
import { screenerService } from '@/features/screener/api/screenerService'
import type {
  Metric,
  ScreenFilter,
  ScreenerPage,
  ScreenerRow,
  ScreenerSector,
  ScreenQuery,
  ScreenSort,
} from '@/schemas/screener'

/** The metric list changes with an mm-be deploy, never inside a session. */
const METRICS_STALE_MS = 60 * 60_000

/** The sector list, likewise. */
const SECTORS_STALE_MS = 60 * 60_000

/** The route's first read: the columns, the filters and the sorts all come from it. */
export const screenerMetricsQueryOptions = () =>
  queryOptions<Metric[]>({
    queryKey: screenerKeys.metrics(),
    queryFn: () => screenerService.metrics(),
    staleTime: METRICS_STALE_MS,
  })

/**
 * The screener's own read of it. Not a suspense read: the screener also mounts where no loader
 * has warmed it (the chart workspace's tab), and draws its own skeleton until it lands. Under the
 * route's loader it is cached before the first frame.
 */
export const useScreenerMetrics = (): UseQueryResult<Metric[]> =>
  useQuery(screenerMetricsQueryOptions())

export const screenerPageQueryOptions = (query: ScreenQuery) =>
  queryOptions<ScreenerPage>({
    queryKey: screenerKeys.page(query),
    queryFn: () => screenerService.screen(query),
  })

/**
 * The page on screen. `keepPreviousData`: a filter, a sort or a page turn keeps the table's rows
 * (the screen draws its skeleton rows over the wait) instead of dropping to the route's pending.
 */
export const useScreenerPage = (query: ScreenQuery): UseQueryResult<ScreenerPage> =>
  useQuery({ ...screenerPageQueryOptions(query), placeholderData: keepPreviousData })

export const screenerCountQueryOptions = (filter: ScreenFilter) =>
  queryOptions<number>({
    queryKey: screenerKeys.count(filter),
    queryFn: () => screenerService.count(filter),
  })

/**
 * How many stocks a filter matches: "of M" with no filter, the Filters dialog's live count with
 * the draft. `keepPreviousData` holds the last count while a new draft's is asked for.
 */
export const useScreenerCount = (
  filter: ScreenFilter,
  options?: { enabled?: boolean },
): UseQueryResult<number> =>
  useQuery({
    ...screenerCountQueryOptions(filter),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  })

export const screenerEveryQueryOptions = (filter: ScreenFilter, sort: ScreenSort | null) =>
  queryOptions<ScreenerRow[]>({
    queryKey: screenerKeys.every(filter, sort),
    queryFn: () => screenerService.every(filter, sort),
  })

/**
 * Every matching stock: the heatmap's read, asked for only while the heatmap is up. The CSV
 * fetches the same key on a click.
 */
export const useScreenerEvery = (
  filter: ScreenFilter,
  sort: ScreenSort | null,
  options?: { enabled?: boolean },
): UseQueryResult<ScreenerRow[]> =>
  useQuery({
    ...screenerEveryQueryOptions(filter, sort),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  })

export const screenerSectorsQueryOptions = () =>
  queryOptions<ScreenerSector[]>({
    queryKey: screenerKeys.sectors(),
    queryFn: ({ client }) => screenerService.sectors(client),
    staleTime: SECTORS_STALE_MS,
  })

/** The Filters dialog's Sector options, and the applied sector tag's name. */
export const useScreenerSectors = (): UseQueryResult<ScreenerSector[]> =>
  useQuery(screenerSectorsQueryOptions())
